export const BUTTON_ACTIONS = [
  { value: 'submit', label: 'Submit' },
  { value: 'next', label: 'Next page' },
  { value: 'previous', label: 'Previous page' },
  { value: 'reset', label: 'Reset' },
  { value: 'custom', label: 'Custom (no action)' },
  { value: 'popup', label: 'Popup button' },
  { value: 'popupWithActions', label: 'Popup with actions' }
] as const

export type ButtonAction = (typeof BUTTON_ACTIONS)[number]['value']

export const POPUP_INNER_ACTIONS = [
  { value: 'close', label: 'Close popup' },
  { value: 'submit', label: 'Submit form' },
  { value: 'next', label: 'Go to next page' },
  { value: 'previous', label: 'Go to previous page' },
  { value: 'reset', label: 'Reset form' },
  { value: 'custom', label: 'No action' }
] as const

export type PopupInnerAction = (typeof POPUP_INNER_ACTIONS)[number]['value']

export interface PopupActionButton {
  id?: string
  text: string
  btnTxtColor: string
  btnBgColor: string
  action: PopupInnerAction
}

export interface ButtonConfig {
  id?: string
  buttonAction: ButtonAction
  text: string
  btnTxtColor: string
  btnBgColor: string
  buttonType?: string
  confirmBeforeAction?: boolean
  confirmTitle?: string
  confirmMessage?: string
  popupTitle?: string
  popupMessage?: string
  popupCloseText?: string
  popupButtons?: PopupActionButton[]
}

export const DEFAULT_CONFIRM_TITLE = 'Please confirm'
export const DEFAULT_CONFIRM_MESSAGES: Record<string, string> = {
  next: 'Are you sure you want to continue to the next page?',
  submit: 'Are you sure you want to submit this form?',
  previous: 'Are you sure you want to go back to the previous page?',
  reset: 'Are you sure you want to reset the form?'
}

export const createDefaultPopupActionButtons = (): PopupActionButton[] => [
  {
    id: `popup-btn-${Date.now()}-cancel`,
    text: 'Cancel',
    btnTxtColor: '#212529',
    btnBgColor: '#f8f9fa',
    action: 'close'
  },
  {
    id: `popup-btn-${Date.now()}-confirm`,
    text: 'Confirm',
    btnTxtColor: '#ffffff',
    btnBgColor: '#0d6efd',
    action: 'submit'
  }
]

export const createDefaultPopupFields = (buttonAction: 'popup' | 'popupWithActions') => ({
  popupTitle: buttonAction === 'popup' ? 'Information' : 'Please confirm',
  popupMessage:
    buttonAction === 'popup'
      ? 'Add your popup message here.'
      : 'Are you sure you want to continue?',
  popupCloseText: 'Close',
  popupButtons: buttonAction === 'popupWithActions' ? createDefaultPopupActionButtons() : []
})

export const createDefaultButton = (buttonAction: ButtonAction): ButtonConfig => ({
  id: `btn-${Date.now()}-${crypto.randomUUID()}`,
  buttonAction,
  text:
    buttonAction === 'submit'
      ? 'Submit'
      : buttonAction === 'next'
      ? 'Next'
      : buttonAction === 'previous'
      ? 'Previous'
      : buttonAction === 'reset'
      ? 'Reset'
      : buttonAction === 'popup'
      ? 'Open popup'
      : buttonAction === 'popupWithActions'
      ? 'Open confirm popup'
      : 'Button',
  btnTxtColor: '#ffffff',
  btnBgColor:
    buttonAction === 'previous'
      ? '#6c757d'
      : buttonAction === 'reset'
      ? '#dc3545'
      : buttonAction === 'custom'
      ? '#6c757d'
      : buttonAction === 'popup' || buttonAction === 'popupWithActions'
      ? '#6610f2'
      : '#007bff',
  buttonType: buttonAction === 'reset' ? 'reset' : buttonAction === 'submit' ? 'submit' : 'button',
  confirmBeforeAction: buttonAction === 'next' || buttonAction === 'submit',
  confirmTitle: DEFAULT_CONFIRM_TITLE,
  confirmMessage: DEFAULT_CONFIRM_MESSAGES[buttonAction] || DEFAULT_CONFIRM_TITLE,
  ...(buttonAction === 'popup' || buttonAction === 'popupWithActions'
    ? createDefaultPopupFields(buttonAction)
    : {})
})

export const createDefaultButtonRow = () => ({
  type: 'buttonRow',
  label: 'Button row',
  alignment: 'end',
  buttons: [createDefaultButton('previous'), createDefaultButton('next')]
})

export const resolveButtonAction = (element: {
  type?: string
  buttonAction?: ButtonAction
  buttonType?: string
}): ButtonAction => {
  if (element.buttonAction) return element.buttonAction
  if (element.type === 'nextPageButton') return 'next'
  if (element.type === 'prevPageButton') return 'previous'
  if (element.buttonType === 'reset') return 'reset'
  if (element.buttonType === 'submit') return 'submit'
  if (element.type === 'button') return 'submit'
  return 'custom'
}

export const isPopupButtonAction = (action: ButtonAction) =>
  action === 'popup' || action === 'popupWithActions'

export const isStandaloneButton = (element: { type?: string }) =>
  element?.type === 'button' ||
  element?.type === 'nextPageButton' ||
  element?.type === 'prevPageButton'

export const isButtonRow = (element: { type?: string }) => element?.type === 'buttonRow'

export const alignmentClass = (alignment?: string) => {
  if (alignment === 'start') return 'justify-content-start'
  if (alignment === 'center') return 'justify-content-center'
  return 'justify-content-end'
}
